import { Box } from '@mui/material';
import { FieldsOrder, formatLabel, getViewFields, LogReportMode, orderFieldsObj, PanelTitle } from '../utils';
import { FIELD_DATATYPES, IRecordField, IRecordFieldsList, IRecordHeaders, IRoleAccess } from 'ui-component/records/types';
import { PanelLoader } from 'ui-component/RecordView/components/PanelLoader';
import { ListValue } from 'views/backoffice/CustomLists/types';
import React, { useMemo, useState, useEffect, useCallback, useContext } from 'react';
import { filterNotAllowedFields } from 'ui-component/records/utils';
import { splitHeaders } from 'ui-component/records/utils/headerHelpers';
import ViewPanel from '../ViewPanel';
import useMyDropdowns from 'hooks/useMyDropdowns';
import EditPanel from '../EditPanel/v2';
import { ObjectProperty } from 'views/backoffice/CustomObjects/types';
import ObjectPanelView from 'ui-component/records/RecordGrid/components/ObjectPanelView';
import ObjectPanelEditForm from 'ui-component/records/RecordGrid/components/ObjectPanelEditForm';
import { LogReportPanel } from '../LogReportPanel';
import { LogReportType } from 'ui-component/RecordView/types';
import { PanelSectionContainer } from './components/PanelSectionContainer';
import { PanelSection } from './components/PanelSection';
import { PanelFormsContext } from 'contexts/panelFormsContext';
import { useFormContext } from 'react-hook-form';

interface AllPanelContainerProps {
    curPanel: PanelTitle;
    loading: boolean;
    fieldAccessList: IRoleAccess[];
    recordData: IRecordHeaders | null;
    listValuesByListId: Record<number, ListValue[]>;
    headerData: IRecordFieldsList | null;
    toggleEditForm: () => void;
    handleOpenViewObjectPanel: (field: IRecordField, fieldName: string, editable: boolean) => void;
    recordTypeId?: string;
    isCreate?: boolean;
    onChangeRecordType: (recordTypeId: string) => void;
    inMemoryObjectProperties: { [key: string]: (ObjectProperty & { value: string })[] };
    rightHandPanelWidth: number;
    handleClosePanel: () => void;
    objectProperties: (ObjectProperty & { value: string; objectName: string })[];
    handleSaveEditObjectFormActions: (
        values: {
            [key: string]: string;
        },
        modalTitle: string
    ) => Promise<void>;
    recordId: number;
    showFullHistory: boolean;
    onCloseFullHistory: () => void;
    logReportMode: LogReportMode;
    handleChangeSelection?: ((newSelection: LogReportMode) => void) | undefined;
    buttonTypeLog?: LogReportType | undefined;
    setButtonTypeLog?: ((newSelection: LogReportType) => void) | undefined;
    toggleLogReport?: (() => void) | undefined;
    inputRefs?: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
    objectEditingRestricted: boolean;
    objectPanelRef?: React.RefObject<{ getValues: () => any; validateObjectChanges: () => void }>;
    ref?: React.RefObject<any>;
    additionalFieldsFormattedNamesToHeaders: Record<string, Partial<IRecordField>>;
}

export const AllPanelContainer = ({
    curPanel,
    loading,
    fieldAccessList,
    recordData,
    listValuesByListId,
    headerData,
    toggleEditForm,
    handleOpenViewObjectPanel,
    recordTypeId,
    inMemoryObjectProperties,
    rightHandPanelWidth,
    handleClosePanel,
    objectProperties,
    handleSaveEditObjectFormActions,
    recordId,
    showFullHistory,
    onCloseFullHistory,
    logReportMode,
    handleChangeSelection,
    buttonTypeLog,
    setButtonTypeLog,
    toggleLogReport,
    isCreate,
    onChangeRecordType,
    inputRefs,
    objectEditingRestricted,
    objectPanelRef,
    additionalFieldsFormattedNamesToHeaders
}: AllPanelContainerProps) => {
    const checkAllowedFields = useMemo(() => filterNotAllowedFields(fieldAccessList), [fieldAccessList]);
    const {
        searchInput,
        sort,
        enableDragAndDrop,
        fieldsOrder: curFieldsOrder,
        customFieldOrder,
        customObjectFieldsOrder,
        setInitialFieldOrder,
        setInitialObjectFieldsOrder,
        loading: loadingUserPreferences,
        setIsCreate
    } = useContext(PanelFormsContext);
    const { baseFieldHeaders, additionalFieldHeaders } = useMemo(
        () => splitHeaders(headerData, checkAllowedFields),
        [checkAllowedFields, headerData]
    );

    const dropdownsData = useMyDropdowns({
        recordTypeId: Number(recordTypeId)
    });

    const { getValues } = useFormContext();

    const editPanelCommonProps = {
        fieldAccessList,
        handleOpenViewObjectPanel,
        inMemoryObjectProperties,
        customListValues: listValuesByListId,
        rightHandPanelWidth,
        dropdownsData,
        isCreate,
        onChangeRecordType,
        recordTypeId,
        additionalFieldsFormattedNamesToHeaders
    };

    const [sectionToRender, setSectionToRender] = useState<any[]>([]);
    const isEditOrViewPanel = [PanelTitle.View, PanelTitle.Edit].includes(curPanel);
    const isObjectPanel = [PanelTitle.ViewObject, PanelTitle.EditObject].includes(curPanel);
    const fieldDataTypes = useMemo(() => ({ ...FIELD_DATATYPES, array: 'array' }), []);

    const preparedString = useCallback((string?: string) => {
        if (!string) return '';
        return string?.toString().trim().toLowerCase();
    }, []);

    const filterBySearchInput = useCallback(
        (items: Record<string, any>, searchTerm?: string) => {
            if (!searchTerm || searchTerm.trim() === '') return items;
            let newSearchTerm = searchTerm;

            if (curPanel.toLowerCase().includes('edit')) {
                const values = getValues();
                const valuesFiltered = Object.fromEntries(
                    Object.entries(values).filter(([key, value]) => value?.toString().toLowerCase().includes(searchTerm.toLowerCase()))
                );

                const hasMatchingValues = Object.keys(valuesFiltered)?.some((value: string) => {
                    const itemsPrepared = Object.keys(items).map((item) => item === value);
                    return itemsPrepared.some((item) => item);
                });

                newSearchTerm = hasMatchingValues ? Object.keys(valuesFiltered)[0]?.substring(0, 3) : searchTerm;
            }

            const filterFn = ([key, data]: [string, any]) => {
                const preparedSearchTerm = preparedString(newSearchTerm);
                const keyMatch = preparedString(key).includes(preparedSearchTerm || '');
                const valueMatch = preparedString(data?.value).includes(preparedSearchTerm || '');
                const labelMatch = preparedString(data?.label).includes(preparedSearchTerm || '');
                const formattedLabelMatch = preparedString(formatLabel(data?.label)).includes(preparedSearchTerm || '');

                return keyMatch || valueMatch || labelMatch || formattedLabelMatch;
            };

            return Object.fromEntries(Object.entries(items).filter(filterFn));
        },
        [curPanel, getValues, preparedString]
    );

    const sortItems = useCallback((items: Record<string, any>, sortOrder?: string) => {
        if (!sortOrder || sortOrder === 'none') return items;

        const sortFn = (a: [string, any], b: [string, any]) => {
            const comparison = a[0].localeCompare(b[0]);
            return sortOrder === 'asc' ? comparison : -comparison;
        };

        return Object.fromEntries(Object.entries(items).sort(sortFn));
    }, []);

    const processGroups = useCallback(
        (groups: any[], searchTerm?: string, sortOrder?: string) =>
            groups
                .map((group) => ({
                    ...group,
                    items: sortItems(filterBySearchInput(group.items || {}, searchTerm), sortOrder)
                }))
                .filter((group) => Object.keys(group.items || {}).length > 0),
        [filterBySearchInput, sortItems]
    );

    const data = useMemo(() => {
        let headersToRender: Record<string, any> = {};
        if (curPanel === PanelTitle.View) {
            headersToRender = getViewFields(recordData, baseFieldHeaders, additionalFieldHeaders, fieldAccessList, listValuesByListId);
        } else if (curPanel === PanelTitle.Edit) {
            const baseFieldHeadersWithFlag = Object.fromEntries(
                Object.entries(baseFieldHeaders).map(([key, value]) => [key, { ...value, isBaseField: true }])
            );
            headersToRender = orderFieldsObj({ ...baseFieldHeadersWithFlag, ...additionalFieldHeaders }, fieldAccessList);
        }

        if (!Object.entries(headersToRender).length) return { dataTypes: [], fieldTypes: [], headersToRender: {} };

        if (customFieldOrder.length > 0) {
            const reorderedHeaders: Record<string, any> = Object.fromEntries(
                customFieldOrder
                    .sort((a, b) => {
                        const orderA = parseInt(a.split('::')[2] || '0', 10);
                        const orderB = parseInt(b.split('::')[2] || '0', 10);
                        return orderA - orderB;
                    })
                    .map((fieldName) => {
                        const [label] = fieldName.split('::');
                        const newLabel = label.replace(/-.*/, '');

                        return [newLabel, headersToRender[newLabel as keyof typeof headersToRender] || []];
                    })
            );

            headersToRender = reorderedHeaders;
        }

        const headersWithoutEmptyArrays = Object.fromEntries(
            Object.entries(headersToRender).filter(([key, value]) => {
                if (Array.isArray(value) && value.length === 0) return false;

                return true;
            })
        );

        const dataTypes = Object.values(
            Object.entries(headersWithoutEmptyArrays as Record<string, any>).reduce((acc: any, [key, field]: [string, any]) => {
                const dataType = fieldDataTypes[field.dataType as keyof typeof fieldDataTypes];
                if (!dataType) return acc;
                if (!acc[dataType]) {
                    acc[dataType] = {
                        type: dataType,
                        items: {}
                    };
                }
                acc[dataType].items[key] = field;
                return acc;
            }, {})
        );

        const fieldTypes = Object.values(
            Object.entries(headersWithoutEmptyArrays as Record<string, any>).reduce((acc: any, [key, field]: [string, any]) => {
                const group = field.isBaseField ? 'Base Fields' : 'Additional Fields';
                if (!acc[group]) {
                    acc[group] = {
                        type: group,
                        items: {}
                    };
                }
                acc[group].items[key] = field;
                return acc;
            }, {})
        );

        const filteredHeadersToRender = sortItems(filterBySearchInput(headersWithoutEmptyArrays, searchInput), sort);
        const filteredDataTypes = processGroups(dataTypes, searchInput, sort);
        const filteredFieldTypes = processGroups(fieldTypes, searchInput, sort);

        const initialFieldOrder = Object.keys(headersWithoutEmptyArrays).map((field) => `${field}-${headersWithoutEmptyArrays[field].id}`);
        setInitialFieldOrder(initialFieldOrder);

        return {
            dataTypes: filteredDataTypes,
            fieldTypes: filteredFieldTypes,
            headersToRender: filteredHeadersToRender,
            searchInput
        };
    }, [
        curPanel,
        recordData,
        baseFieldHeaders,
        additionalFieldHeaders,
        fieldAccessList,
        listValuesByListId,
        searchInput,
        sort,
        customFieldOrder,
        fieldDataTypes,
        filterBySearchInput,
        sortItems,
        processGroups,
        setInitialFieldOrder
    ]);

    const objectData = useMemo(() => {
        if (!Array.isArray(objectProperties) || objectProperties.length === 0) {
            return { objectPropertiesToRender: [], objectDataTypes: [] };
        }

        const filteredObjectProperties = objectProperties.filter((property) => property.enabled && property.isDisplayable);

        const initialObjectFieldsOrder = filteredObjectProperties.map((property) => `${property.objectName};;${property.name}`);

        setInitialObjectFieldsOrder(initialObjectFieldsOrder);

        if (
            (!searchInput || searchInput.trim() === '') &&
            (!sort || sort === 'none') &&
            (!enableDragAndDrop || (customFieldOrder.length === 0 && customObjectFieldsOrder.length === 0))
        ) {
            const objectDataTypes = filteredObjectProperties.reduce((acc: any, property: ObjectProperty & { value: string }) => {
                const dataType = fieldDataTypes[property.dataType as keyof typeof fieldDataTypes];
                if (!dataType) return acc;
                if (!acc[dataType]) {
                    acc[dataType] = { type: dataType, items: [] };
                }
                acc[dataType].items.push(property);
                return acc;
            }, {});

            return {
                objectPropertiesToRender: filteredObjectProperties,
                objectDataTypes: Object.values(objectDataTypes)
            };
        }

        let result = [...filteredObjectProperties];

        if (customObjectFieldsOrder.length > 0) {
            const orderMap = new Map();
            customObjectFieldsOrder.forEach((fieldKey, index) => {
                orderMap.set(fieldKey, index);
            });

            const objectFields = result.filter((property) => {
                const fieldKey = `${property.objectName};;${property.name}`;
                return orderMap.has(fieldKey);
            });

            const nonObjectFields = result.filter((property) => {
                const fieldKey = `${property.objectName};;${property.name}`;
                return !orderMap.has(fieldKey);
            });

            objectFields.sort((a, b) => {
                const fieldKeyA = `${a.objectName};;${a.name}`;
                const fieldKeyB = `${b.objectName};;${b.name}`;
                const orderA = orderMap.get(fieldKeyA) ?? Number.MAX_SAFE_INTEGER;
                const orderB = orderMap.get(fieldKeyB) ?? Number.MAX_SAFE_INTEGER;
                return orderA - orderB;
            });

            result = [...objectFields, ...nonObjectFields];
        }

        if (searchInput?.trim()) {
            const searchTerm = searchInput.toLowerCase().trim();
            result = result.filter((property) => {
                if (!property?.name) return false;
                return property.name.toLowerCase().includes(searchTerm);
            });
        }

        if (sort && sort !== 'none' && result.length > 0) {
            result.sort((a, b) => {
                const nameA = a?.name?.toLowerCase() || '';
                const nameB = b?.name?.toLowerCase() || '';
                return sort === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
            });
        }

        const objectDataTypes =
            result.length > 0
                ? result.reduce((acc: any, property: ObjectProperty & { value: string }) => {
                      const dataType = fieldDataTypes[property.dataType as keyof typeof fieldDataTypes];
                      if (!dataType) return acc;
                      if (!acc[dataType]) {
                          acc[dataType] = { type: dataType, items: [] };
                      }
                      acc[dataType].items.push(property);
                      return acc;
                  }, {})
                : {};

        return {
            objectPropertiesToRender: result,
            objectDataTypes
        };
    }, [
        objectProperties,
        searchInput,
        sort,
        enableDragAndDrop,
        customFieldOrder,
        fieldDataTypes,
        customObjectFieldsOrder,
        setInitialObjectFieldsOrder
    ]);

    useEffect(() => {
        setIsCreate(isCreate || false);
    }, [isCreate, setIsCreate]);

    useEffect(() => {
        if (curFieldsOrder !== FieldsOrder.Default) {
            if ('dataTypes' in data && 'fieldTypes' in data) {
                setSectionToRender(curFieldsOrder === FieldsOrder.ByDataType ? (data as any).dataTypes : (data as any).fieldTypes);
            }
        }
    }, [curFieldsOrder, data]);

    if (loading || loadingUserPreferences) return <PanelLoader />;

    return (
        <Box height="100%" width="100%">
            {isEditOrViewPanel && (
                <>
                    {curFieldsOrder === FieldsOrder.Default ? (
                        <>
                            {curPanel === PanelTitle.View && (
                                <ViewPanel
                                    fields={data.headersToRender}
                                    handleOpenViewObjectPanel={handleOpenViewObjectPanel}
                                    headerData={headerData}
                                    doubleClickAction={toggleEditForm}
                                    rightHandPanelWidth={rightHandPanelWidth}
                                    isRecordViewer
                                />
                            )}

                            {curPanel === PanelTitle.Edit && (
                                <EditPanel
                                    {...editPanelCommonProps}
                                    headersToRender={data.headersToRender}
                                    inputRefs={inputRefs}
                                    rightHandPanelWidth={rightHandPanelWidth}
                                    isRecordViewer
                                />
                            )}
                        </>
                    ) : (
                        <PanelSectionContainer>
                            {sectionToRender.length > 0 &&
                                sectionToRender.map((section) => (
                                    <PanelSection key={section.type} title={section.type}>
                                        {curPanel === PanelTitle.View ? (
                                            <ViewPanel
                                                fields={section.items}
                                                handleOpenViewObjectPanel={handleOpenViewObjectPanel}
                                                headerData={headerData}
                                                doubleClickAction={toggleEditForm}
                                                showFieldMenu={false}
                                                rightHandPanelWidth={rightHandPanelWidth}
                                                isRecordViewer
                                            />
                                        ) : (
                                            <EditPanel
                                                {...editPanelCommonProps}
                                                headersToRender={section.items}
                                                inputRefs={inputRefs}
                                                showFieldMenu={false}
                                                rightHandPanelWidth={rightHandPanelWidth}
                                                isRecordViewer
                                            />
                                        )}
                                    </PanelSection>
                                ))}
                        </PanelSectionContainer>
                    )}
                </>
            )}

            {isObjectPanel && objectProperties && (
                <>
                    {curFieldsOrder === FieldsOrder.Default ? (
                        <>
                            {curPanel === PanelTitle.ViewObject && (
                                <ObjectPanelView
                                    loadingObjectProperties={loading}
                                    handleClosePanel={handleClosePanel}
                                    objectProperties={objectData.objectPropertiesToRender}
                                    rightHandPanelWidth={rightHandPanelWidth}
                                    isRecordViewer
                                    objectEditingRestricted={objectEditingRestricted}
                                />
                            )}

                            {curPanel === PanelTitle.EditObject && (
                                <ObjectPanelEditForm
                                    ref={objectPanelRef}
                                    objectProperties={objectData.objectPropertiesToRender}
                                    handleUpdateObjectViewer={handleSaveEditObjectFormActions}
                                    handleClosePanelViewer={handleSaveEditObjectFormActions}
                                    objectEditingRestricted={objectEditingRestricted}
                                    rightHandPanelWidth={rightHandPanelWidth}
                                    isRecordViewer
                                />
                            )}
                        </>
                    ) : (
                        <PanelSectionContainer>
                            {objectData.objectDataTypes.length > 0 &&
                                objectData.objectDataTypes.map((section: any) => (
                                    <PanelSection key={section.type} title={section.type}>
                                        {curPanel === PanelTitle.ViewObject ? (
                                            <ObjectPanelView
                                                loadingObjectProperties={loading}
                                                handleClosePanel={handleClosePanel}
                                                objectProperties={section.items}
                                                showFieldMenu={false}
                                                rightHandPanelWidth={rightHandPanelWidth}
                                                isRecordViewer
                                                objectEditingRestricted={objectEditingRestricted}
                                            />
                                        ) : (
                                            <ObjectPanelEditForm
                                                ref={objectPanelRef}
                                                objectProperties={section.items}
                                                handleUpdateObjectViewer={handleSaveEditObjectFormActions}
                                                handleClosePanelViewer={handleSaveEditObjectFormActions}
                                                objectEditingRestricted={objectEditingRestricted}
                                                showFieldMenu={false}
                                                rightHandPanelWidth={rightHandPanelWidth}
                                                isRecordViewer
                                            />
                                        )}
                                    </PanelSection>
                                ))}
                        </PanelSectionContainer>
                    )}
                </>
            )}

            {curPanel === PanelTitle.LogReport && (
                <LogReportPanel
                    recordId={Number(recordId)}
                    showFullHistory={showFullHistory}
                    onCloseFullHistory={onCloseFullHistory}
                    logReportMode={logReportMode}
                    onChangeSelection={handleChangeSelection}
                    buttonTypeLog={buttonTypeLog}
                    setButtonTypeLog={setButtonTypeLog}
                    toggleLogReport={toggleLogReport}
                />
            )}
        </Box>
    );
};
